import {UserFlag} from 'common/report/constants';
import {apiClient} from 'common/util/api-client';
import {AsyncCountingSemaphore} from 'common/util/async-counting-semaphore';

const BASE_URL = `/api/app/me/user-flags`;

export type UserFlagResults = Record<UserFlag, number>;

export function getUserFlags(): Promise<UserFlagResults> {
  return apiClient.get<UserFlagResults>(BASE_URL);
}

export async function setUserFlag(flagName: UserFlag): Promise<void> {
  await apiClient.post(`${BASE_URL}/set`, {flag: flagName});
}

export async function incrementUserFlag(flagName: UserFlag): Promise<void> {
  await apiClient.post(`${BASE_URL}/increment/`, {flag: flagName});
}

/**
 *  We keep track of the number of reports a user has viewed so that we
 *  can prompt them to invite users or check out dashboards after viewing
 *  a certain number of reports.
 *  This class is a singleton wrapper around the increment method, allowing us to expose
 *  a promise in order to await the latest update for first time users
 */
class ViewedReportFlagManager {
  private viewedReportPool: AsyncCountingSemaphore;

  constructor() {
    this.viewedReportPool = new AsyncCountingSemaphore(1);
  }

  private async incrementViewedReportCountFlag(): Promise<void> {
    await incrementUserFlag(UserFlag.ViewedReportCount);
  }

  public async increment() {
    if (await this.viewedReportPool.acquire()) {
      await this.incrementViewedReportCountFlag();
      this.viewedReportPool.release();
    }
  }

  public async waitForPendingIncrements(): Promise<void> {
    if (await this.viewedReportPool.acquire()) {
      this.viewedReportPool.release();
    }
  }
}

export const viewedReportFlagManager = new ViewedReportFlagManager();
